/**
 * Allowlist for raw fields that intentionally don't map to normalized
 *
 * Some CollectedData fields are used only for:
 * - UI presentation/narrative
 * - Conditional logic that doesn't directly feed the engine
 * - Intermediate calculations
 *
 * This allowlist documents and permits these intentional gaps.
 */

import type { AllowlistEntry } from './types';

/**
 * Raw paths that are allowed to not have normalized equivalents.
 * Each entry must have a documented reason.
 */
export const unmappedAllowlist: AllowlistEntry[] = [
  {
    rawPath: 'usagePattern',
    reason: 'UI presentation only - affects narrative framing, not engine calculation',
  },
  {
    rawPath: 'usageFrequency',
    reason: 'UI presentation only - provides context but hotWaterDemand captures the engine-relevant info',
  },
  {
    rawPath: 'lifeStage',
    reason: 'UI presentation only - used for narrative personalization',
  },
  {
    rawPath: 'outdoorSpace',
    reason: 'Used for heat pump feasibility assessment but not directly mapped - affects UI flow',
  },
  {
    rawPath: 'sectionStatuses',
    reason: 'Pure UI state - tracks which sections user has reviewed',
  },
  {
    rawPath: 'sedbukRating',
    reason: 'Deprecated legacy field - superseded by sedbukBand',
  },
  {
    rawPath: 'considerCombi',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerSystem',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerRegular',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerHeatPump',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerGSHP',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerHybrid',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerStorageCombi',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerVentedCylinder',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerMixergyVented',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerUnventedCylinder',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerMixergyUnvented',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'considerOnDemand',
    reason: 'UI preference that affects option generation, not a direct engine input',
  },
  {
    rawPath: 'customerReference',
    reason: 'Reporting metadata - displayed in report, not used by engine',
  },
  {
    rawPath: 'documentReference',
    reason: 'Reporting metadata - displayed in report, not used by engine',
  },
  {
    rawPath: 'reportDate',
    reason: 'Reporting metadata - displayed in report, not used by engine',
  },
  {
    rawPath: 'contaminationAssessment',
    reason: 'UI flow control - determines whether to show detailed contamination questions',
  },
  {
    rawPath: 'radiatorColdBottom',
    reason: 'Contamination symptom - mapped via contaminationSymptoms object when assessment enabled',
  },
  {
    rawPath: 'kettlingWhistling',
    reason: 'Contamination symptom - mapped via contaminationSymptoms object when assessment enabled',
  },
  {
    rawPath: 'gurglingAir',
    reason: 'Contamination symptom - mapped via contaminationSymptoms object when assessment enabled',
  },
  {
    rawPath: 'pumpNoise',
    reason: 'Contamination symptom - mapped via contaminationSymptoms object when assessment enabled',
  },
  {
    rawPath: 'frequentBleeding',
    reason: 'Contamination symptom - mapped via contaminationSymptoms object when assessment enabled',
  },
  {
    rawPath: 'slowWarmup',
    reason: 'Contamination symptom - mapped via contaminationSymptoms object when assessment enabled',
  },
  {
    rawPath: 'visibleMagnetite',
    reason: 'Contamination symptom - mapped via contaminationSymptoms object when assessment enabled',
  },
  {
    rawPath: 'systemType',
    reason: 'Contamination system factor - mapped via contaminationSystemFactors object',
  },
  {
    rawPath: 'hasMagneticFilter',
    reason: 'Contamination system factor - mapped via contaminationSystemFactors object',
  },
  {
    rawPath: 'hardWaterArea',
    reason: 'Contamination system factor - mapped via contaminationSystemFactors object',
  },
  {
    rawPath: 'storyEducationSelections',
    reason: 'UI presentation only - determines which educational elements appear in the customer story',
  },
  // LCOO core fields - dynamically constructed into lcoo object
  {
    rawPath: 'enableLcoo',
    reason: 'LCOO feature flag - triggers construction of lcoo object in SystemRecInput',
  },
  {
    rawPath: 'lcooPeriodYears',
    reason: 'LCOO config - dynamically mapped to lcoo.periodYears',
  },
  {
    rawPath: 'lcooGasTariff',
    reason: 'LCOO config - dynamically mapped to lcoo.tariffs.gasPPerKwh',
  },
  {
    rawPath: 'lcooElecTariff',
    reason: 'LCOO config - dynamically mapped to lcoo.tariffs.elecPPerKwh',
  },
  // LCOO per-option fields - dynamically mapped via lcoo.perOption object
  {
    rawPath: 'lcooOptionA_upfront',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.A',
  },
  {
    rawPath: 'lcooOptionA_beforeGrants',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.A',
  },
  {
    rawPath: 'lcooOptionA_applyBUS',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.A',
  },
  {
    rawPath: 'lcooOptionA_maintenance',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.A',
  },
  {
    rawPath: 'lcooOptionA_retainCylinder',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.A',
  },
  {
    rawPath: 'lcooOptionA_cylinderAge',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.A',
  },
  {
    rawPath: 'lcooOptionA_cylinderCost',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.A',
  },
  {
    rawPath: 'lcooOptionB_upfront',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.B',
  },
  {
    rawPath: 'lcooOptionB_beforeGrants',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.B',
  },
  {
    rawPath: 'lcooOptionB_applyBUS',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.B',
  },
  {
    rawPath: 'lcooOptionB_maintenance',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.B',
  },
  {
    rawPath: 'lcooOptionB_retainCylinder',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.B',
  },
  {
    rawPath: 'lcooOptionB_cylinderAge',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.B',
  },
  {
    rawPath: 'lcooOptionB_cylinderCost',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.B',
  },
  {
    rawPath: 'lcooOptionC_upfront',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.C',
  },
  {
    rawPath: 'lcooOptionC_beforeGrants',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.C',
  },
  {
    rawPath: 'lcooOptionC_applyBUS',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.C',
  },
  {
    rawPath: 'lcooOptionC_maintenance',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.C',
  },
  {
    rawPath: 'lcooOptionC_retainCylinder',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.C',
  },
  {
    rawPath: 'lcooOptionC_cylinderAge',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.C',
  },
  {
    rawPath: 'lcooOptionC_cylinderCost',
    reason: 'LCOO per-option field - dynamically mapped to lcoo.perOption.C',
  },
];

/**
 * Check if a raw path is in the allowlist
 */
export function isAllowlisted(rawPath: string): boolean {
  return unmappedAllowlist.some(entry => entry.rawPath === rawPath);
}

/**
 * Get the reason why a path is allowlisted
 */
export function getAllowlistReason(rawPath: string): string | undefined {
  const entry = unmappedAllowlist.find(e => e.rawPath === rawPath);
  return entry?.reason;
}
